import { SubmitData } from '@adyen/adyen-web';
import { CategoryType } from '../../../../shared/components/CategoryLabel/models';
import { Address, PhoneNumber } from '../../../../shared/models/ProfileModel';
import emptySliceApi from '../../../../shared/store/emptyApiSlice';
import { ProductBundleOption } from '../../models';
import { SavedPayment } from './models';
import {
  LocalChapter,
  RedemptionProfileEnquiry,
  SelectOption,
} from './ProfileUpdateModal/models';

export interface OrderPayload {
  sku: string;
  store?: string;
  // eslint-disable-next-line camelcase
  bundle_options?: ProductBundleOption[];
  // eslint-disable-next-line camelcase
  enroll_autorenew?: boolean;
}

export interface BillingAddressPayload {
  id?: number;
  address1: string;
  address2: string;
  city: string;
  state: string;
  stateLabel?: string;
  country: string;
  postalCode: string;
}

export const apiSlice = emptySliceApi.injectEndpoints({
  endpoints: (builder) => ({
    placeOrder: builder.mutation<
      { orderId: string },
      {
        accountId: string;
        dealId?: string;
        payload: OrderPayload;
      }
    >({
      query: ({ accountId, dealId, payload }) => ({
        url: `v4/accounts/${accountId}/deals/${dealId}/orders`,
        method: 'POST',
        body: payload,
      }),
    }),
    redemptionEnquiry: builder.mutation<
      RedemptionProfileEnquiry,
      { productType: CategoryType; accountId: string; dealIds: string[] }
    >({
      query: ({ productType, accountId, dealIds }) => ({
        url: '/profile/redemptionEnquiry',
        method: 'POST',
        body: {
          ProductType: productType,
          AccountId: accountId,
          DealIds: dealIds,
        },
      }),
    }),
    statesQuery: builder.mutation<SelectOption[], { country: string }>({
      query: ({ country }) => ({
        url: `/options/states/${country}`,
        method: 'GET',
      }),
    }),
    updateProfile: builder.mutation<
      void,
      { address: Address; phoneNumber: PhoneNumber }
    >({
      query: ({ address, phoneNumber }) => ({
        url: `/profile`,
        method: 'POST',
        body: { address, phoneNumber },
      }),
    }),
    createAdyenPayment: builder.mutation<
      unknown,
      {
        state: SubmitData;
        billingAddress: BillingAddressPayload;
      }
    >({
      query: ({ state, billingAddress }) => ({
        url: '/payment/methods/adyen',
        method: 'POST',
        body: {
          ...state?.data,
          billingAddress: {
            ...billingAddress,
            street1: billingAddress.address1,
            street2: billingAddress.address2,
            country: {
              iso3Code: billingAddress.country,
            },
          },
        },
      }),
    }),
    createRazorpayOrder: builder.mutation<
      { id?: string; customer?: { id?: string } },
      {
        billingAddress: BillingAddressPayload;
        contact: string;
      }
    >({
      query: ({ billingAddress, contact }) => ({
        url: '/payment/methods/razorpay/order',
        method: 'POST',
        body: {
          billingAddress: {
            ...billingAddress,
            street1: billingAddress.address1,
            street2: billingAddress.address2,
            country: {
              iso3Code: billingAddress.country,
            },
          },
          contact,
        },
      }),
    }),
    getLocalChapters: builder.query<
      LocalChapter[],
      { latitude?: number; longitude?: number; distance?: number }
    >({
      query: ({ latitude, longitude, distance }) => ({
        url: `/chapters/localChapters?latitude=${latitude || ''}&longitude=${
          longitude || ''
        }&distance=${distance || ''}`,
        method: 'GET',
      }),
    }),
    getSavedPayment: builder.query<SavedPayment[], void>({
      query: () => ({
        url: `/payment/methods/saved`,
        method: 'GET',
      }),
      providesTags: ['SavedPayment'],
    }),
    setPrimaryPaymentMethod: builder.mutation<
      void,
      {
        accountReferenceId: number;
        paymentVendor: string;
        pmiAddressId: number;
        pmiPaymentId: number;
        tokenId: string;
        isDefault: boolean;
      }
    >({
      query: (params) => ({
        url: '/payment/methods/primary',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: ['SavedPayment'],
    }),
  }),
});

export const {
  usePlaceOrderMutation,
  useRedemptionEnquiryMutation,
  useStatesQueryMutation,
  useUpdateProfileMutation,
  useCreateAdyenPaymentMutation,
  useCreateRazorpayOrderMutation,
  useGetLocalChaptersQuery,
  useGetSavedPaymentQuery,
  useSetPrimaryPaymentMethodMutation,
} = apiSlice;
